// Colours: Jurkovic Consulting tokens (design/tokens.css) + the 12-colour sticky palette.
export type RGB = [number, number, number];

export const STICKY: { name: string; rgb: RGB }[] = [
  { name: "Gelb", rgb: [255, 255, 204] }, { name: "Grau", rgb: [245, 246, 248] }, { name: "Bernstein", rgb: [255, 241, 118] },
  { name: "Limette", rgb: [213, 246, 146] }, { name: "Grün", rgb: [129, 199, 132] }, { name: "Blau", rgb: [166, 204, 245] },
  { name: "Petrol", rgb: [103, 198, 192] }, { name: "Cyan", rgb: [35, 191, 231] }, { name: "Orange", rgb: [245, 159, 85] },
  { name: "Rosa", rgb: [255, 206, 224] }, { name: "Rot", rgb: [229, 115, 115] }, { name: "Violett", rgb: [190, 136, 199] },
];

export const NAVY: RGB = [0, 35, 75];
export const BLUE: RGB = [19, 118, 191];
export const CYAN: RGB = [2, 187, 238];
export const SKY: RGB = [173, 216, 244];
export const CANVAS: RGB = [243, 243, 243];
export const WHITE: RGB = [255, 255, 255];
export const LANE_ALT: RGB = [247, 250, 252];
export const CAPTION: RGB = [232, 241, 249];
/** Date rows of masterplan months: a lighter sky, so sprint and masterplan read as two parts. */
export const MONTH_SKY: RGB = [214, 234, 248];
/** This week's red frame (the red of conflicts). */
export const THIS_WEEK: RGB = [214, 69, 69];
export const BACKLOG: RGB = [214, 69, 69];
export const TEXT: RGB = [0, 35, 75];
export const MUTED: RGB = [91, 107, 125];

/** Status colours (keep in sync with --status-* tokens). Index = status id. */
export const STATUS: Record<number, { label: string; rgb: RGB }> = {
  1: { label: "Offen", rgb: [19, 118, 191] },
  2: { label: "Erledigt", rgb: [0, 196, 140] },
  4: { label: "Wartend", rgb: [245, 166, 35] },
  3: { label: "Abgebrochen", rgb: [214, 69, 69] },
};

/** Link colours (index = stored colour). Red is reserved for broken dependencies. */
export const LINK_COLORS: { name: string; rgb: RGB }[] = [
  { name: "Navy", rgb: [0, 35, 75] }, { name: "Blau", rgb: [19, 118, 191] }, { name: "Cyan", rgb: [2, 160, 210] },
  { name: "Petrol", rgb: [0, 128, 120] }, { name: "Grün", rgb: [0, 160, 110] }, { name: "Orange", rgb: [230, 140, 20] },
  { name: "Grau", rgb: [120, 132, 146] },
];
export const CONFLICT: RGB = [214, 69, 69];

/** Load of a lane vs its capacity (X5): green below 80 %, amber up to 100 %, red above (dark
 *  enough for white text). */
export const LOAD_OK: RGB = [0, 128, 90];
export const LOAD_FULL: RGB = [168, 98, 0];
export const LOAD_OVER: RGB = [196, 50, 50];
export const loadColor = (ratio: number): RGB => (ratio > 1 ? LOAD_OVER : ratio >= 0.8 ? LOAD_FULL : LOAD_OK);

/**
 * Person colours (A4/K4): a person picks one in the account menu (stored with the account); it
 * colours the header of their cards, their avatar and their pointer. Dark enough for white text.
 */
export const PERSON_COLORS: { name: string; rgb: RGB }[] = [
  { name: "Navy", rgb: [0, 35, 75] }, { name: "Blau", rgb: [19, 118, 191] }, { name: "Petrol", rgb: [0, 112, 140] },
  { name: "Türkis", rgb: [0, 128, 120] }, { name: "Grün", rgb: [0, 128, 80] }, { name: "Oliv", rgb: [92, 108, 20] },
  { name: "Orange", rgb: [190, 88, 10] }, { name: "Rot", rgb: [184, 48, 72] }, { name: "Violett", rgb: [120, 60, 140] },
  { name: "Graphit", rgb: [73, 73, 73] },
];
const chosen = new Map<string, number>();
/** Short name → chosen colour, from GET /api/people; others keep the colour from their short name. */
export function setPersonColors(people: { short: string; color?: number | null }[]) {
  chosen.clear();
  for (const p of people) if (p.color != null && PERSON_COLORS[p.color]) chosen.set(p.short.toUpperCase(), p.color);
  dispatchEvent(new Event("lanewerk:colors"));
}
function chosenColor(key: string): RGB | null {
  const c = chosen.get(key.toUpperCase());
  return c === undefined ? null : PERSON_COLORS[c].rgb;
}

/** Header colour per owner short name: the person's colour, else a stable hash into dark brand tones. */
const OWNER_TONES: RGB[] = [[0, 35, 75], [19, 118, 191], [11, 76, 140], [73, 73, 73], [0, 120, 110], [120, 60, 140]];
export function ownerColor(owner: string): RGB {
  const c = chosenColor(owner);
  if (c) return c;
  let h = 0;
  for (const ch of owner) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return OWNER_TONES[h % OWNER_TONES.length];
}

/** Colour per person (avatars, pointers): bright enough on the navy bar and on the white board. */
const PEOPLE_TONES: RGB[] = [[19, 118, 191], [0, 128, 120], [120, 60, 140], [206, 104, 18], [0, 150, 100], [186, 52, 92], [80, 86, 170]];
export function personColor(key: string): RGB {
  const c = chosenColor(key);
  if (c) return c;
  let h = 7;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PEOPLE_TONES[h % PEOPLE_TONES.length];
}

export const css = (c: RGB, a = 1) => (a === 1 ? `rgb(${c[0]},${c[1]},${c[2]})` : `rgba(${c[0]},${c[1]},${c[2]},${a})`);
